<template lang="pug">

div.condition

  //- Select the operator (within the last x days, today, etc.)
  v-select(
    class="filter__operator"
    :items="operators"
    item-text="name"
    v-model="selectedOperator"
    @input="changedOperator"
    return-object
  )

  //- Used for date selection
  v-menu(
    :close-on-content-click="false"
    class="filter__value"
    v-model="isDatePickerMenuShown"
    :nudge-right="40"
    transition="scale-transition"
    lazy
    offset-y
    full-width
    v-if="selectedOperator && selectedOperator.needDateValue"
  )
    v-text-field(
      slot="activator"
      v-model="selectedValue"
      prepend-icon="event"
      readonly
    )
    v-date-picker(
      no-title
      scrollable
      :show-current='selectedValue || true'
      v-model='selectedValue'
      @input="isDatePickerMenuShown = false"
    )

  //- When not a date, use this plain text field for numbers
  // integer text field is small
  v-text-field(
    class="filter__value short"
    type="number"
    v-model="selectedValue"
    v-if="selectedOperator && selectedOperator.needIntegerValue"
  )
  v-spacer

</template>

<script>

import dateFns from 'date-fns';
import getOperatorsByField from '@/Core/utilities/getOperatorsByField';

const defaultSelectedValue = null;
const dateFormat = 'YYYY-MM-DD';
const defaultRelativeDays = 7;
const defaultCalendarDate = dateFns.format(new Date(), dateFormat);

export default {
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    options: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      /**
       * The operator from the list that's been selected
       * @type {Object}
       */
      selectedOperator: null,
      /**
       * The selected condition (operand and value)
       * @type {Object}
       */
      selectedValue: null,

      isDatePickerMenuShown: false,
    };
  },
  mounted() {
    this.setSelectedOperator();
    this.selectedValue = this.initSelectedValue();

    this.$watch('selectedOperator', operator => {
      this.emitSelected();
    });

    this.$watch('selectedValue', selectedValue => {
      this.emitSelected();
    });
  },
  computed: {

    /**
     * Check if the current value is a date-formatted string
     * like '2018-06-15'
     *
     * @return {Boolean}
     */
    isSelectedValueDate() {
      const dateRegex = /\d{4}-\d{2}-\d{2}/g;
      return dateRegex.test(this.selectedValue);
    },

    /**
     * If it is a number, NaN will fail.
     *
     * Flip the boolean to check if it _is_ a number
     *
     * @return {Boolean}
     */
    isSelectedValueInteger() {
      const integerRegex = /^\d+$/g;
      return integerRegex.test(this.selectedValue);
    },
    operators() {
      return getOperatorsByField(this.value.field);
    }
  },
  methods: {
    /**
     * The operator has been changed; update/clear the value as necessary
     * @param  {string} operator
     */
    changedOperator({ needDateValue, needIntegerValue }) {

      // if needs a date and value isn't a date, set to today
      if (needDateValue === true && !this.isSelectedValueDate) {
        this.selectedValue = defaultCalendarDate;
      } else if (needIntegerValue === true && !this.isSelectedValueInteger) {
        this.selectedValue = defaultRelativeDays;
      } else if (!needDateValue && !needIntegerValue) {
        this.selectedValue = defaultSelectedValue;
      }
    },

    /**
     * Check if the requested operator is relative.
     *
     * @param  {}  operatorName
     * @return {Boolean}
     */
    isRelative(operatorName) {
      const operators = this.operators.filter(operator => operator.name === operatorName);
      if (operators.length === 1) {
        return operators[0].isRelative || false;
      }
    },

    /**
     * Set the selected operator depending on what's been passed in as selected
     */
    setSelectedOperator() {
      this.selectedOperator = this.operators.filter(operator => {
        return operator.name === this.value.operator;
      });
      if (this.selectedOperator.length) this.selectedOperator = this.selectedOperator[0];
    },

    /**
     * Wrapper to emit condition operator & value
     */
    emitSelected() {
      this.$emit('input', {
        operator: this.selectedOperator.name,
        value: this.selectedValue,
      });
    },

    /**
     * Use the selected value if it exists;
     * Otherwise, populate with a default:
     *  - if relative, use the default number (within the last 5 days)
     *  - if absolute, use today's date
     */
    initSelectedValue() {
      if (this.value.value) return this.value.value;

      return this.isRelative(this.selectedOperator) ?
        defaultRelativeDays :
        defaultCalendarDate;
    },
  },

};
</script>
